import type { User } from '../types'
import type { ModuleDevFlags } from './moduleDevAccess'
import { userBypassesModuleDev } from './moduleDevAccess'
import { hasFinancistAccess } from './roleFinancialAccess'
import { DOVOLENA_MODULE_DESCRIPTION, DOVOLENA_MODULE_TITLE } from './dovolenaRequestType'

export type CabinetLinkKind = 'shortcut' | 'module'

export type CabinetLink = {
  id: string
  kind: CabinetLinkKind
  icon: string
  title: string
  description: string
  to?: string
  href?: string
  external?: boolean
  disabled?: boolean
  devMode?: boolean
  blockedMessage?: string
}

export const INTRANET_SHAREPOINT_DOCS_HREF =
  'https://isscheb-my.sharepoint.com/:f:/g/personal/vasyliev_iss-cheb_cz/IgDIFdap3A8WT4FsJTx4D_GMATTZ5QdSq1l9fM-tj5De0Q8?e=A31iu0'

function canFksp(user: User | null, flags: ModuleDevFlags | null): boolean {
  const dev = flags?.fksp ?? true
  return !dev || (user != null && userBypassesModuleDev('fksp', user))
}

function canDovolena(user: User | null, flags: ModuleDevFlags | null): boolean {
  const dev = flags?.dovolena ?? true
  return !dev || (user != null && userBypassesModuleDev('dovolena', user))
}

function canTravel(user: User | null, flags: ModuleDevFlags | null): boolean {
  const dev = flags?.travel_orders ?? true
  return !dev || (user != null && userBypassesModuleDev('travel_orders', user))
}

/** Vstup do Docházkového systému — všichni přihlášení (výběr role uvnitř). */
export function canEnterAttendanceHub(user: User | null): boolean {
  return !!user
}

/** České označení role z databáze (pro zobrazení v kabinetu). */
export function formatAssignedRole(user: User | null): string {
  if (!user?.role) return '—'
  const isIT = user.role === 'admin' && user.admin_type === 'spravce_it'
  const isBudovy = user.role === 'admin' && user.admin_type === 'spravce_budov'
  if (isIT) return 'Administrátor IT'
  if (isBudovy) return 'Administrátor budov'
  const map: Record<string, string> = {
    admin: 'Administrátor',
    director: 'Ředitel',
    financist: 'Hlavní ekonom',
    referent: 'Obchodní referent',
    referent_eko: 'Referent EKO',
    vedouci: 'Vedoucí',
    vedouci_executor: 'Řešitel–vedoucí',
    executor: 'Řešitel',
    requester: 'Žadatel',
    property_referent: 'Referent správy majetku',
    spravce_rozvrhu: 'Správce rozvrhů',
    spravce_jidelny: 'Správce jídelny',
    personalista: 'Personalista',
    zocmos: 'ZO ČMOS',
    zastupce_reditele: 'Zástupce ředitele',
  }
  return map[user.role] || user.role
}

export function buildCabinetShortcutLinks(
  user: User | null,
  flags: ModuleDevFlags | null
): CabinetLink[] {
  if (!user) return []
  const isIT = user.role === 'admin' && user.admin_type === 'spravce_it'
  const fkspOk = canFksp(user, flags)
  const out: CabinetLink[] = []

  if (fkspOk && (user.role === 'personalista' || isIT)) {
    out.push({
      id: 'sh-fksp-personalista',
      kind: 'shortcut',
      icon: '🏦',
      title: 'FKSP — personalista',
      description: 'Schvalování z pohledu personalisty',
      to: '/fksp/personalista',
    })
  }

  if (fkspOk && (user.role === 'zocmos' || isIT)) {
    out.push({
      id: 'sh-fksp-zocmos',
      kind: 'shortcut',
      icon: '🏦',
      title: 'FKSP — ZO ČMOS',
      description: 'Úkoly ZO ČMOS u žádostí FKSP',
      to: '/fksp/zocmos',
    })
  }

  if (fkspOk && (user.role === 'director' || isIT)) {
    out.push({
      id: 'sh-fksp-director',
      kind: 'shortcut',
      icon: '🏦',
      title: 'FKSP — ředitel',
      description: 'Schvalování FKSP',
      to: '/fksp/director',
    })
  }

  if (fkspOk && (hasFinancistAccess(user) || isIT)) {
    out.push({
      id: 'sh-fksp-ekonom',
      kind: 'shortcut',
      icon: '🏦',
      title: 'FKSP — hlavní ekonom',
      description: 'Ekonomická část FKSP',
      to: '/fksp/ekonom',
    })
  }

  if (user.role === 'spravce_jidelny' || user.role === 'director' || isIT) {
    out.push({
      id: 'sh-canteen-stats',
      kind: 'shortcut',
      icon: '📊',
      title: 'Jídelna — statistiky',
      description: 'Přehled hlasování (správce jídelny / ředitel)',
      to: '/public-canteen/statistics',
    })
  }

  return out
}

export function buildCabinetModuleLinks(
  user: User | null,
  flags: ModuleDevFlags | null
): CabinetLink[] {
  const devFksp = flags?.fksp ?? true
  const devDovolena = flags?.dovolena ?? true
  const devTravel = flags?.travel_orders ?? true
  const canFk = canFksp(user, flags)
  const canDov = canDovolena(user, flags)
  const canTr = canTravel(user, flags)
  const canEnterAttendance = canEnterAttendanceHub(user)

  const devMsgFksp =
    'Modul je uzavřen. Přístup mají ředitel, správce IT a hlavní ekonom.'
  const devMsgDovolena = devMsgFksp
  const devMsgTravel =
    'Modul je uzavřen. Přístup mají ředitel, zástupce ředitele, správce IT a hlavní ekonom.'

  const out: CabinetLink[] = [
    {
      id: 'mod-tickets',
      kind: 'module',
      icon: '🎫',
      title: 'Závady a požadavky',
      description: 'Správa budov + IT',
      to: '/app/select-role',
    },
    {
      id: 'mod-purchase',
      kind: 'module',
      icon: '🛒',
      title: 'Žádanky o nákup',
      description: 'Správa žádostí o nákup',
      to: '/purchase-requests/select-role',
    },
    {
      id: 'mod-property',
      kind: 'module',
      icon: '🏢',
      title: 'Správy majetku',
      description: 'Správa a evidence majetku',
      to: '/property/select-role',
    },
    {
      id: 'mod-canteen',
      kind: 'module',
      icon: '🍽️',
      title: 'Školní jídelna',
      description: 'Hodnocení kvality jídel a obsluhy',
      to: '/public-canteen',
    },
    {
      id: 'mod-calendar',
      kind: 'module',
      icon: '📅',
      title: 'Kalendář akcí školy',
      description: 'Školní akce a programy',
      to: '/calendar',
    },
    {
      id: 'mod-calendar-events',
      kind: 'module',
      icon: '🏫',
      title: 'Kalendář využití školních prostor',
      description: 'Rezervace tělocvičen, aul a učeben',
      to: '/calendar-events',
    },
    {
      id: 'mod-docs',
      kind: 'module',
      icon: '📁',
      title: 'Školní dokumenty',
      description: 'Dokumenty a sdílené soubory',
      href: INTRANET_SHAREPOINT_DOCS_HREF,
      external: true,
    },
    {
      id: 'mod-employees',
      kind: 'module',
      icon: '👥',
      title: 'Seznam zaměstnanců',
      description: 'Kontakty a telefony zaměstnanců školy',
      to: '/school-employees',
    },
    {
      id: 'mod-it-info',
      kind: 'module',
      icon: '💻',
      title: 'Informace od IT',
      description: 'Důležité informace od IT administrátora',
      to: '/it-admin-info',
    },
    {
      id: 'mod-fksp',
      kind: 'module',
      icon: '🏦',
      title: 'Žádosti FKSP',
      description: 'Příspěvky z fondu FKSP',
      to: '/fksp/select-role',
      disabled: !canFk,
      devMode: devFksp,
      blockedMessage: devMsgFksp,
    },
    {
      id: 'mod-dovolena',
      kind: 'module',
      icon: '🏖️',
      title: DOVOLENA_MODULE_TITLE,
      description: DOVOLENA_MODULE_DESCRIPTION,
      to: '/dovolena/select-role',
      disabled: !canDov,
      devMode: devDovolena,
      blockedMessage: devMsgDovolena,
    },
    {
      id: 'mod-travel',
      kind: 'module',
      icon: '✈️',
      title: 'Cestovní příkaz',
      description: 'Správa cestovních příkazů',
      to: '/travel-orders',
      disabled: !canTr,
      devMode: devTravel,
      blockedMessage: devMsgTravel,
    },
  ]

  out.push({
    id: 'mod-attendance',
    kind: 'module',
    icon: '🕒',
    title: 'Docházkový systém',
    description: canEnterAttendance
      ? 'Výběr role: uživatel, vedoucí, personalista (Admin IT)'
      : 'Přihlaste se pro vstup do docházky',
    to: '/attendance',
    disabled: !canEnterAttendance,
    devMode: true,
    blockedMessage: 'Docházkový systém vyžaduje přihlášení.',
  })

  out.push({
    id: 'mod-vehicle-fleet',
    kind: 'module',
    icon: '🚗',
    title: 'Rezervace vozidel',
    description: canEnterAttendance
      ? 'Systém rezervace školního autoprovozu pro řidiče školy'
      : 'Přihlaste se pro vstup',
    to: '/vehicle-fleet',
    disabled: !canEnterAttendance,
    devMode: true,
    blockedMessage: 'Rezervace vozidel vyžaduje přihlášení.',
  })

  return out
}
